import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { SetupProgressService, tourTarget } from 'src/app/services/setup-progress.service';
import { TPipe } from 'src/app/i18n/t.pipe';

interface Box { top: number; left: number; width: number; height: number; }

/**
 * The step-by-step guide after sign-up: on each step's page it dims the screen, lights up the button to click
 * (data-tour="…") and points at it with an arrow. When the step is done (the checklist checks it from the real data)
 * it moves to the next one by itself. Off a step's page it shows a small card to open it. Only for companies that
 * signed up by themselves, and only while the owner has the guide on.
 */
@Component({
  selector: 'app-setup-guide',
  standalone: true,
  imports: [CommonModule, TPipe],
  template: `
    @if (on && current; as cur) {
      @if (target && !dismissedHere && !dialogOpen) {
        <div class="sg-spot" [style.top.px]="target.top - 6" [style.left.px]="target.left - 6"
             [style.width.px]="target.width + 12" [style.height.px]="target.height + 12" aria-hidden="true"></div>
        <div class="sg-bubble" role="dialog" aria-live="polite" [attr.aria-label]="'su.step.' + cur.step.key | t"
             [class.sg-above]="above" [style.top.px]="bubbleTop" [style.left.px]="bubbleLeft" data-test="guide-bubble">
          <svg class="sg-arrow" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
               [style.left.px]="arrowLeft"><path d="M12 19V5"></path><path d="M5 12l7-7 7 7"></path></svg>
          <span class="sg-count">{{ 'tour.of' | t: { n: cur.index, total: cur.total } }}</span>
          <b class="sg-title">{{ 'su.step.' + cur.step.key | t }}</b>
          <p>{{ textKey | t }}</p>
          <div class="sg-actions">
            <button type="button" class="sg-primary" (click)="gotIt()" data-test="guide-ok">{{ 'tour.gotIt' | t }}</button>
            @if (cur.step.optional) {
              <button type="button" (click)="skipStep(cur.step.key)" data-test="guide-skip">{{ 'tour.skip' | t }}</button>
            }
            <button type="button" class="sg-link" (click)="stop()" data-test="guide-stop">{{ 'tour.stop' | t }}</button>
          </div>
        </div>
      } @else if (!dialogOpen) {
        <div class="sg-card" role="status" aria-live="polite" data-test="guide-card">
          <span class="sg-count">{{ 'tour.of' | t: { n: cur.index, total: cur.total } }}</span>
          <b class="sg-title">{{ 'su.step.' + cur.step.key | t }}</b>
          <p>{{ (onStepPage ? (dismissedHere ? 'tour.waiting' : textKey) : 'tour.notHere') | t: { step: ('su.step.' + cur.step.key | t) } }}</p>
          <div class="sg-actions">
            @if (!onStepPage) {
              <button type="button" class="sg-primary" (click)="open()" data-test="guide-open">{{ 'tour.open' | t }}</button>
            }
            @if (cur.step.optional) {
              <button type="button" (click)="skipStep(cur.step.key)">{{ 'tour.skip' | t }}</button>
            }
            <button type="button" class="sg-link" (click)="stop()">{{ 'tour.stop' | t }}</button>
          </div>
        </div>
      }
    } @else if (finished) {
      <div class="sg-card sg-done" role="status" data-test="guide-done">
        <b class="sg-title">✓ {{ 'tour.done' | t }}</b>
        <div class="sg-actions"><button type="button" class="sg-primary" (click)="closeFinished()">{{ 'tour.gotIt' | t }}</button></div>
      </div>
    }
  `,
  styles: [`
    .sg-spot { position: fixed; z-index: 900; border-radius: 12px; pointer-events: none;
      box-shadow: 0 0 0 4px #3b82f6, 0 0 0 9999px rgba(15, 23, 42, .45); transition: all .25s ease; }
    .sg-bubble, .sg-card { position: fixed; z-index: 901; width: min(340px, calc(100vw - 32px)); background: #fff; color: #111827;
      border-radius: 14px; padding: 16px 16px 14px; box-shadow: 0 12px 32px rgba(15, 23, 42, .28); display: grid; gap: 6px;
      font-size: 14px; line-height: 1.5; }
    .sg-card { right: 16px; bottom: 16px; border: 1px solid #e5e7eb; }
    .sg-bubble p, .sg-card p { margin: 0; }
    .sg-count { font-size: 12px; font-weight: 600; color: #1d4ed8; text-transform: uppercase; letter-spacing: .4px; }
    .sg-title { font-size: 16px; }
    .sg-arrow { position: absolute; top: -40px; color: #3b82f6; animation: sg-bounce 1s ease-in-out infinite; }
    .sg-above .sg-arrow { top: auto; bottom: -40px; transform: rotate(180deg); animation-name: sg-bounce-down; }
    @keyframes sg-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
    @keyframes sg-bounce-down { 0%, 100% { transform: rotate(180deg) translateY(0); } 50% { transform: rotate(180deg) translateY(-6px); } }
    @media (prefers-reduced-motion: reduce) { .sg-arrow { animation: none; } .sg-spot { transition: none; } }
    .sg-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; align-items: center; }
    .sg-actions button { height: 40px; padding: 0 14px; border-radius: 10px; border: 1.5px solid #d1d5db; background: #fff;
      color: #111827; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
    .sg-actions .sg-primary { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
    .sg-actions .sg-link { border: 0; background: none; color: #4b5563; padding: 0 6px; font-weight: 500; text-decoration: underline; }
    .sg-actions button:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }
    .sg-done { border-color: #86efac; }
    :host-context(.dark-theme) .sg-bubble, :host-context(.dark-theme) .sg-card { background: #2a3447; color: #eaeff4; border-color: #3c4a61; }
    :host-context(.dark-theme) .sg-actions button { background: #202936; color: #eaeff4; border-color: #4a5a75; }
    :host-context(.dark-theme) .sg-actions .sg-primary { background: #3b82f6; border-color: #3b82f6; color: #fff; }
    :host-context(.dark-theme) .sg-actions .sg-link { background: none; color: #a7b4c7; }
  `]
})
export class SetupGuideComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private zone = inject(NgZone);
  progress = inject(SetupProgressService);

  current: ReturnType<SetupProgressService['currentGuideStep']> = null;
  target: Box | null = null;
  bubbleTop = 0;
  bubbleLeft = 0;
  arrowLeft = 20;
  above = false;
  dismissedHere = false;
  dialogOpen = false;
  onStepPage = false;
  finished = false;
  textKey = '';

  private sub?: Subscription;
  private follow?: ReturnType<typeof setInterval>;
  private poll?: ReturnType<typeof setInterval>;
  private wasOnWithSteps = false;

  get on(): boolean {
    // Solo el dueño o un Admin: el guía quedaba prendido en el navegador y se le mostraba al chofer que entraba después.
    return this.progress.guideOn && this.progress.managed() && this.progress.canLoad();
  }

  ngOnInit(): void {
    try {
      if (new URLSearchParams(window.location.search).get('guide') === '1') this.progress.startGuide();
    } catch { /* no window */ }

    this.sub = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.dismissedHere = false;
      if (this.on) this.progress.refresh();
      this.locate();
    });
    if (this.progress.canLoad()) this.progress.refresh();

    this.follow = setInterval(() => this.zone.run(() => this.locate()), 400);
    // Cada 5 s mientras la guía está prendida: al terminar un paso (agregar la facility) pasa al siguiente enseguida.
    this.poll = setInterval(() => { if (this.on) this.progress.refresh(); }, 5000);
    this.locate();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.follow) clearInterval(this.follow);
    if (this.poll) clearInterval(this.poll);
  }

  private pathOf(url: string): string {
    return url.split('#')[0].split('?')[0].replace(/\/+$/, '');
  }

  locate(): void {
    if (!this.on) {
      this.current = null;
      this.target = null;
      return;
    }
    this.current = this.progress.currentGuideStep();
    if (this.current) this.wasOnWithSteps = true;
    if (!this.current) {
      this.target = null;
      if (this.wasOnWithSteps && this.progress.allRequiredDone()) {
        this.finished = true;
        this.progress.stopGuide();
      }
      return;
    }

    this.textKey = tourTarget(this.current.step.key, this.current.step.link).text;
    this.onStepPage = this.pathOf(this.router.url) === this.pathOf(this.current.step.link || '');
    this.dialogOpen = !!document.querySelector('.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container mat-dialog-container');
    if (!this.onStepPage) {
      this.target = null;
      return;
    }

    const where = tourTarget(this.current.step.key, this.current.step.link);
    this.textKey = where.text;
    const el = document.querySelector(`[data-tour="${where.target}"]`) as HTMLElement | null;
    const r = el?.getBoundingClientRect();
    if (!el || !r || (r.width === 0 && r.height === 0)) {
      this.target = null;
      return;
    }
    this.target = { top: r.top, left: r.left, width: r.width, height: r.height };

    const bubbleW = Math.min(340, window.innerWidth - 32);
    const bubbleH = 190;
    const spaceBelow = window.innerHeight - r.bottom;
    this.above = spaceBelow < bubbleH + 60 && r.top > bubbleH + 60;
    this.bubbleTop = this.above ? r.top - bubbleH - 52 : r.bottom + 52;
    const center = r.left + r.width / 2;
    this.bubbleLeft = Math.max(16, Math.min(center - bubbleW / 2, window.innerWidth - bubbleW - 16));
    this.arrowLeft = Math.max(12, Math.min(center - this.bubbleLeft - 17, bubbleW - 46));
  }

  open(): void {
    const link = this.current?.step.link;
    if (!link) return;
    const [path, fragment] = link.split('#');
    this.router.navigateByUrl(fragment ? `${path}#${fragment}` : path);
  }

  gotIt(): void {
    this.dismissedHere = true;
  }

  skipStep(key: string): void {
    this.progress.skip(key);
    this.dismissedHere = false;
    this.locate();
  }

  stop(): void {
    this.progress.stopGuide();
    this.current = null;
    this.target = null;
  }

  closeFinished(): void {
    this.finished = false;
  }
}
